/**
 * A match's position, derived from its move log.
 *
 * Nothing about the board is transmitted — the server sends the log and both sides replay
 * it through the engine, which is what makes it impossible for the two to disagree. That
 * replay is memoised on the log itself, because the 2023 client ran move generation three
 * separate times on every React render and then again inside every cell's callback.
 */

import { useMemo } from 'react';

import {
  Game,
  type MatchStatus,
  type Move,
  parseMove,
  type PlayerIndex,
  threatenedKings,
} from '@gok/engine';
import type { MatchState } from '@gok/shared';

import { LIVE } from './history.ts';

export interface MatchGame {
  readonly game: Game;
  /** Empty when viewing history, or when the match is over. */
  readonly legalMoves: readonly Move[];
  readonly toMove: PlayerIndex;
  /** Cells holding a king that the enemy can currently take. */
  readonly threatened: readonly number[];
  /** The move that produced this position, for highlighting where the piece came from. */
  readonly lastMove: Move | undefined;
  readonly status: MatchStatus;
}

export function useMatchGame(
  match: MatchState | undefined,
  ply: number = LIVE,
): MatchGame | undefined {
  // The key is the log itself: two positions with the same moves are the same position,
  // and nothing else about the match can change it.
  const log = match?.moves.map((m) => m.san).join(' ') ?? '';
  const upTo = match ? Math.min(ply, match.moves.length) : 0;
  const variantKey = match ? JSON.stringify(match.variant) : '';

  return useMemo(() => {
    if (!match) return undefined;

    const game = new Game(match.variant);
    let lastMove: Move | undefined;

    for (let i = 0; i < upTo; i++) {
      const move = parseMove(game.position, match.moves[i].san);
      if (!move) {
        // Only reachable if the log and the engine disagree, which would be a bug
        // worth seeing rather than rendering around.
        console.error(`Cannot replay ${match.moves[i].san} at ply ${i}`);
        break;
      }
      game.play(move);
      lastMove = move;
    }

    const live = upTo === match.moves.length;
    return {
      game,
      // History is read-only: offering moves in a position that has already been played
      // past is how the old client managed to look interactive and do nothing.
      legalMoves: live && !game.isOver ? game.legalMoves() : [],
      toMove: game.position.playerToMove,
      threatened: threatenedKings(game.position),
      lastMove,
      status: live ? match.status : 'playing',
    };
    // `log` and `variantKey` stand in for `match`, which is a new object every event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantKey, log, upTo]);
}
